import React, { forwardRef, useId, useLayoutEffect, useRef, useState } from "react";

// Real refraction needs an SVG filter as a backdrop-filter, which only Chromium supports.
// Elsewhere the glass falls back to the blur + rim highlights in `.lg` (App.css).
const refracts =
  typeof navigator !== "undefined" &&
  /Chrome\/\d+/.test(navigator.userAgent) &&
  typeof ResizeObserver !== "undefined";

const mapCache = new Map();

// Displacement map for a rounded rect. Pixels within `bezel` of the edge sample the backdrop
// from further inside the shape, so the rim bends what's behind it like thick glass.
// Red/green encode the x/y offset around a neutral 128.
const displacementMap = (w, h, radius, bezel) => {
  const key = `${w}x${h}:${radius}:${bezel}`;
  if (mapCache.has(key)) return mapCache.get(key);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  const img = ctx.createImageData(w, h);
  const d = img.data;
  const hw = w / 2;
  const hh = h / 2;
  const r = Math.min(radius, hw, hh);

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      // Signed distance to the rounded rect and its outward normal.
      const px = x + 0.5 - hw;
      const py = y + 0.5 - hh;
      const qx = Math.abs(px) - (hw - r);
      const qy = Math.abs(py) - (hh - r);
      let nx;
      let ny;
      let sdf;
      if (qx > 0 && qy > 0) {
        const len = Math.hypot(qx, qy);
        sdf = len - r;
        nx = (Math.sign(px) * qx) / len;
        ny = (Math.sign(py) * qy) / len;
      } else if (qx > qy) {
        sdf = qx - r;
        nx = Math.sign(px);
        ny = 0;
      } else {
        sdf = qy - r;
        nx = 0;
        ny = Math.sign(py);
      }

      const inside = -sdf;
      let strength = 0;
      if (inside >= 0 && inside < bezel) {
        const k = 1 - inside / bezel;
        strength = k * k;
      }

      const i = (y * w + x) * 4;
      d[i] = 128 - nx * strength * 127;
      d[i + 1] = 128 - ny * strength * 127;
      d[i + 2] = 128;
      d[i + 3] = 255;
    }
  }

  ctx.putImageData(img, 0, 0);
  const url = canvas.toDataURL();
  mapCache.set(key, url);
  return url;
};

// A Liquid Glass surface. `radius` is a pixel radius or "pill" (half the height);
// `bezel` is how far in from the edge the glass bends light, `depth` how strongly.
// The material itself (blur and tone) lives in CSS variables on `.lg`; `blur` overrides the blur,
// and `tone={false}` (`.lg--clear`) skips the dimming, e.g. for the slider lens.
const LiquidGlass = forwardRef(
  (
    { as: Tag = "div", radius = "pill", bezel = 14, depth = 22, blur, tone = true, className = "", style, children, ...rest },
    forwardedRef
  ) => {
    const localRef = useRef(null);
    const filterId = `lg${useId().replace(/:/g, "")}`;
    const [size, setSize] = useState(null);

    useLayoutEffect(() => {
      const el = localRef.current;
      if (!refracts || !el) return undefined;
      const update = () => {
        const w = Math.round(el.offsetWidth);
        const h = Math.round(el.offsetHeight);
        if (w && h) setSize((s) => (s && s.w === w && s.h === h ? s : { w, h }));
      };
      update();
      const observer = new ResizeObserver(update);
      observer.observe(el);
      return () => observer.disconnect();
    }, []);

    const setRefs = (el) => {
      localRef.current = el;
      if (typeof forwardedRef === "function") forwardedRef(el);
      else if (forwardedRef) forwardedRef.current = el;
    };

    // The CSS corners and the refraction map share one radius so they can't disagree.
    const cornerRadius = radius === "pill" ? 9999 : radius;
    const r = size ? (radius === "pill" ? size.h / 2 : radius) : 0;
    // Frost first, then bend the frosted image at the rim, then tone it.
    const chain = `blur(var(--lg-blur)) url(#${filterId}) var(--lg-tone)`;
    const refraction = size ? { backdropFilter: chain, WebkitBackdropFilter: chain } : null;
    const blurVar = blur === undefined ? null : { "--lg-blur": `${blur}px` };

    return (
      <Tag ref={setRefs} className={`lg ${tone ? "" : "lg--clear"} ${className}`} style={{ borderRadius: cornerRadius, ...blurVar, ...style, ...refraction }} {...rest}>
        {size && (
          <svg className="lg-defs" width="0" height="0" aria-hidden="true" focusable="false">
            <filter
              id={filterId}
              x="0"
              y="0"
              width={size.w}
              height={size.h}
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feImage
                href={displacementMap(size.w, size.h, r, bezel)}
                x="0"
                y="0"
                width={size.w}
                height={size.h}
                preserveAspectRatio="none"
                result="map"
              />
              <feDisplacementMap in="SourceGraphic" in2="map" scale={depth} xChannelSelector="R" yChannelSelector="G" />
            </filter>
          </svg>
        )}
        {children}
      </Tag>
    );
  }
);

export default LiquidGlass;
